// src/App.tsx
//
// Root component. Wraps the app in AuthProvider + PortalProvider
// and routes to the appropriate page.

import { Route, Routes } from "react-router-dom";
import { FeatureGate } from "@/components/FeatureGate";
import { Layout } from "@/components/layout/Layout";
import { AuthProvider } from "@/components/auth/AuthContext";
import { FavoritesProvider } from "@/components/feature/FavoritesProvider";
import { PortalProvider } from "@/lib/portal/PortalContext";
import { CustomersProvider } from "@/lib/customers/useCustomers";
import { ShippingProvider } from "@/lib/shipping/shippingStore";
import { RepairsProvider } from "@/lib/repairs/useRepairs";
import { CampaignsProvider } from "@/lib/campaigns/useCampaigns";
import { SettingsProvider } from "@/lib/settings/useSettings";
import { Home } from "@/pages/Home";
import { Catalog } from "@/pages/Catalog";
import { ProductDetail } from "@/pages/ProductDetail";
import { Stores } from "@/pages/Stores";
import { QrGenerator } from "@/pages/QrGenerator";
import { Login } from "@/pages/Login";
import { NotFound } from "@/pages/NotFound";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { AdminDashboard } from "@/pages/admin/Dashboard";
import { AdminApprovals } from "@/pages/admin/Approvals";
import { AdminProducts } from "@/pages/admin/Products";
import { AdminCustomers } from "@/pages/admin/Customers";
import { AdminBrands } from "@/pages/admin/Brands";
import { AdminCategories } from "@/pages/admin/Categories";
import { ShippingDashboard, ShipmentDetail } from "@/pages/shipping/ShippingView";
import { PortalLayout } from "@/pages/portal/PortalLayout";
import { PortalDashboard } from "@/pages/portal/Dashboard";
import { PortalCatalog } from "@/pages/portal/Catalog";
import { PortalFavorites } from "@/pages/portal/Favorites";
import { PortalCart } from "@/pages/portal/Cart";
import { PortalOrders } from "@/pages/portal/Orders";
import { CustomerInvoices } from "@/components/feature/CustomerInvoices";
import { RepairsList, RepairDetail } from "@/pages/portal/Repairs";
import { AdminRepairsQueue } from "@/pages/admin/Repairs";
import { AdminQuotations } from "@/pages/admin/Quotations";
import { AdminLending } from "@/pages/admin/Lending";
import { CampaignsList, CampaignDetail } from "@/pages/admin/Campaigns";
import {
  FilemakerSyncLayoutDetail,
  FilemakerSyncPage,
} from "@/pages/admin/FilemakerSync";
import { SettingsPage } from "@/pages/admin/Settings";
import { AdminImports } from "@/pages/admin/Imports";
import { FmSyncClientPage } from "@/pages/admin/FmSyncClient";
import { AdminOrders } from "@/pages/admin/Orders";
import { AdminExports } from "@/pages/admin/Exports";
import { AdminInvoices } from "@/pages/admin/Invoices";
import { UsersPage } from "@/pages/admin/Users";
import { AdminInventory } from "@/pages/admin/Inventory";
import { SalesDashboard } from "@/pages/admin/Sales";
import { Incentives } from "@/pages/admin/Incentives";

export function App() {
  return (
    <AuthProvider>
      <FavoritesProvider>
      <CustomersProvider>
        <ShippingProvider>
          <RepairsProvider>
            <CampaignsProvider>
              <SettingsProvider>
                  <PortalProvider>
                    <Routes>
          <Route element={<Layout />}>
            {/* Public site (Day 3) */}
            <Route index element={<Home />} />
            <Route path="catalog" element={<Catalog />} />
            <Route path="catalog/:sku" element={<ProductDetail />} />
            <Route path="stores" element={<Stores />} />
            <Route path="qr" element={<QrGenerator />} />
            <Route path="login" element={<Login />} />

            {/* Admin (Day 4) */}
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route
                path="approvals"
                element={
                  <FeatureGate flag="adminApprovals">
                    <AdminApprovals />
                  </FeatureGate>
                }
              />
              <Route
                path="products"
                element={
                  <FeatureGate flag="adminProducts">
                    <AdminProducts />
                  </FeatureGate>
                }
              />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="brands" element={<AdminBrands />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="shipping" element={<ShippingDashboard />} />
              <Route path="shipping/:shipmentId" element={<ShipmentDetail />} />
              <Route path="repairs" element={<AdminRepairsQueue />} />
              <Route path="quotations" element={<AdminQuotations />} />
              <Route path="lending" element={<AdminLending />} />
              <Route
                path="campaigns"
                element={
                  <FeatureGate flag="adminCampaigns">
                    <CampaignsList />
                  </FeatureGate>
                }
              />
              <Route
                path="campaigns/:campaignId"
                element={
                  <FeatureGate flag="adminCampaigns">
                    <CampaignDetail />
                  </FeatureGate>
                }
              />
              <Route
                path="filemaker-sync"
                element={
                  <FeatureGate flag="adminFilemakerSync">
                    <FilemakerSyncPage />
                  </FeatureGate>
                }
              />
              <Route path="imports" element={<AdminImports />} />
              <Route
                path="fm-sync-client"
                element={
                  <FeatureGate flag="adminFmSyncClient">
                    <FmSyncClientPage />
                  </FeatureGate>
                }
              />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="exports" element={<AdminExports />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route
                path="filemaker-sync/layout/:layoutKey"
                element={
                  <FeatureGate flag="adminFilemakerSync">
                    <FilemakerSyncLayoutDetail />
                  </FeatureGate>
                }
              />
              <Route path="sales" element={<SalesDashboard />} />
              <Route path="incentives" element={<Incentives />} />
              <Route
                path="users"
                element={
                  <FeatureGate flag="adminUsers">
                    <UsersPage />
                  </FeatureGate>
                }
              />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Customer portal (Day 5) */}
            <Route path="portal" element={<PortalLayout />}>
              <Route index element={<PortalDashboard />} />
              <Route path="catalog" element={<PortalCatalog />} />
              <Route path="favorites" element={<PortalFavorites />} />
              <Route path="cart" element={<PortalCart />} />
              <Route path="orders" element={<PortalOrders />} />
              <Route path="orders/:orderId" element={<PortalOrders />} />
              <Route path="invoices" element={<CustomerInvoices />} />
              <Route path="repairs" element={<RepairsList />} />
              <Route path="repairs/:ticketId" element={<RepairDetail />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
                </Routes>
              </PortalProvider>
              </SettingsProvider>
            </CampaignsProvider>
          </RepairsProvider>
        </ShippingProvider>
      </CustomersProvider>
      </FavoritesProvider>
    </AuthProvider>
  );
}
